<template>
    <div class="sidebar" id="sidebar">

        <!-- Drawer -->
        <div class="sidebar__drawer sidebar__section" 
            :class="{'is-header-swiped': headerSwiped}"
            v-touch:start.disablePassive.prevent="sidebarHeaderTouchStartHandler" 
            v-touch:end="sidebarHeaderTouchEndHandler" 
            v-touch:swipe.top="sidebarHeaderSwipeUpHandler" 
            v-touch:swipe.bottom="sidebarHeaderSwipeDownHandler"
            v-touch:moving="sidebarHeaderMovingHandler"
        >
        </div>
        <InterimStopNote 
            v-if="!mobile"
            :interimStopNote="interimStopMessageVisible && activeTransportModeNoIntermediateStops"
            @close="interimStopNoteCloseHandler"
        />
        <component 
            :is="(nativeScrolling || !scrollbarStatus) ? 'div' : 'PerfectScrollbar'" 
            ref="sidebarScrollbar" 
            :options="nativeScrolling ? false : {suppressScrollX: true}" 
            id="sidebarScroll"
            :class="{'sidebar__scrollbar--native': nativeScrolling, 'sidebar__scrollbar' : scrollbarStatus}" 
        >
            <div class="sidebar__pindrop sidebar__section sidebar__section--border-bottom" v-if="showPinDrop">
                <PinDropResult/>
            </div>

            <!-- Header -->
            <div class="sidebar__header sidebar__section" v-if="!showPinDrop">
                <h1 class="sidebar__title h1">Routenplaner</h1>
                
                <div class="sidebar__reset sidebar__action-icon" v-show="showClearResults" @click="resetClickHandler">
                    <SmrTooltip content="Zurücksetzen" position="right">
                        <icon><IconClose /></icon>
                    </SmrTooltip>
                </div>
            </div>

            <!-- Search fields -->
            <SearchFields
                :visible="!showPinDrop"
                :waypoints="waypoints"
                :showSearchOrderOptions="showSearchOrderOptions"
                :waypointSortIndex="waypointSortIndex"
                :collapseEnabled="searchCollapseEnabled"
                :isWaypointsDragged="isWaypointsDragged"
                @change="searchChangeHandler"
                @addInterimClick="addInterimClickHandler"
                @removeInterimClick="removeInterimClickHandler"
                @reverseClick="reverseClickHandler"
                @waypointDraggedChange="waypointDraggedChangeHandler"
                @updateScrollPosition="updateScrollPositionHandler"
                @optionMoved="optionMovedHandler"
            />

            <!-- Route options -->
            <div class="sidebar__route-options sidebar__section" v-if="!showPinDrop">
                <SmrTooltip content="Abfahrt/Ankunft ändern" position="bottom">
                    <div class="sidebar__route-options__time" @click="routeOptionsTimeClickHandler">
                        <icon class="sidebar__route-options__time__icon"><IconClock /></icon>
                        <div class="sidebar__route-options__time__text"><strong>{{ this.shownRouteTime.mode === 'arrival' ? 'An' : 'Ab' }}: </strong><span class="sidebar__route-options__time__text__detail" v-html="this.shownRouteTime.isNow ? 'Jetzt' : formattedRouteTime"></span></div>
                    </div>
                </SmrTooltip>
                <TimeOptions 
                    ref="time-options" 
                    type="route"
                    title="Reisedatum wählen"
                />
                <SmrTooltip content="Routeneinstellungen" position="bottom-start">
                    <div class="sidebar__route-options__settings" @click="routeOptionsSettingsClickHandler">
                        <div class="sidebar__route-options__settings__text">Optionen</div>
                        <!-- <div class="sidebar__route-options__settings__icon" :class="{'options-changed' : routeSettingsChanged}"> -->
                        <div class="sidebar__route-options__settings__icon" :class="{'options-changed' : routeSettingsUpdated}">
                              <icon>
                                <IconSettings />
                            </icon>
                        </div>
                    </div>
                </SmrTooltip>
            </div>

            <!-- Submit button -->
            <div class="sidebar__submit sidebar__section" v-if="!showPinDrop && showSearchButton">
                <SmrButton @click.native="submitClickHandler" :loading="routesLoading" :icon="true">
                    <span slot="icon">
                        <icon><IconRoute /></icon>
                    </span>
                    {{ searchButtonLabel }}
                </SmrButton>
            </div>

            <!-- General no results message -->
            <div class="sidebar__section sidebar__section--border-top" v-if="showNoResults">
                <NoResultsMessage />
            </div>

            <!-- Single point result -->
            <div class="sidebar__single-location sidebar__section sidebar__section--border-top sidebar__section--border-bottom" v-if="filledWaypointCount === 1 && !showPinDrop">
                <SinglePointResult :waypoint="filledWaypoints[0]" />
            </div>

            <!-- Route results -->
            <!-- <div class="sidebar__routes" v-if="(someTransportModesLoaded || routeSessionInProgress) && filledWaypointCount > 1"> -->
            <div class="sidebar__routes" v-if="showRouteResults">
                <RouteResults :waypointCount="waypointCount"/>
            </div>

            <!-- POI Picker -->
            <div 
                class="sidebar__section sidebar__section--border-top-huge" 
                :class="[{'sidebar__section--border-bottom': (POIMode === 'specific' || POIMode === 'route') && !selectedPOIs.length}]"
                v-show="poiPickerVisible"
            >
                <POIPicker :mode="POIMode" :visible="poiPickerVisible"/>
            </div>

            <!-- POI Results -->
            <div v-if="selectedPOIs.length > 0">
                <POIResults :active="!sidebarDetailOpen"/>
            </div>

            <!-- Saved routes -->
            <SavedRoutes mode="summary"  v-if="isEmptyOverview"/>

            <!-- Login teaser -->
            <LoginTeaser mode="summary"  v-if="isEmptyOverview && !user"/>
        </component>
        
        <SidebarDetail />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

import SearchFields from '@/components/SearchFields.vue'
import SinglePointResult from '@/components/SinglePointResult.vue'
import InterimStopNote from '@/components/InterimStopNote.vue'
import TimeOptions from '@/components/TimeOptions.vue'
import SmrButton from '@/components/SmrButton.vue'
import SmrTooltip from '@/components/SmrTooltip.vue'
import POIPicker from '@/components/POIPicker.vue'
import SidebarDetail from '@/components/SidebarDetail.vue'
import Icon from '@/components/Icon.vue'
import IconClose from '@/components/icons/IconClose.vue'
import IconRoute from '@/components/icons/IconRoute.vue'
import IconClock from '@/components/icons/IconClock.vue'
import IconSettings from '@/components/icons/IconSettings.vue'
import eventBus from '@/utils/event-bus';
import { formatDate } from '@/utils/format'
import { POI_TRANSPORT_MODES} from '@/data/transportModesTypes'
import { updateRouteUrl, updateQueryParams } from '@/utils/url-utils'
import { trackEvent, trackGa4Event } from '@/utils/tracking'
import isEmpty from 'lodash/isEmpty'
import VueCookies from 'vue-cookies'
import { TOGGLE_INTERIM_COLLAPSE_AMOUNT } from '@/data/interimInfo'
import { CUSTOM_LOCATION_TYPES } from '@/data/locationTypes'

const LOGGEDIN_WAYPOINT_LIMIT = 100
const LOGGEDOUT_WAYPOINT_LIMIT = 7

export default {
    components: {
        SearchFields,
        SinglePointResult,
        InterimStopNote,
        SmrButton,
        SmrTooltip,
        Icon,
        IconClose,
        IconRoute,
        IconClock,
        IconSettings,
        RouteResults: () => import(/* webpackChunkName: "RouteResults" */ '@/components/RouteResults.vue'),
        NoResultsMessage: () => import(/* webpackChunkName: "NoResultsMessage" */ '@/components/NoResultsMessage.vue'),
        POIPicker,
        SavedRoutes: () => import(/* webpackChunkName: "SavedRoutes" */ '@/components/SavedRoutes.vue'),
        TimeOptions,
        POIResults: () => import(/* webpackChunkName: "POIResults" */ '@/components/POIResults.vue'),
        PinDropResult: () => import(/* webpackChunkName: "PinDropResult" */ '@/components/PinDropResult.vue'),
        SidebarDetail,
        LoginTeaser: () => import(/* webpackChunkName: "LoginTeaser" */ '@/components/LoginTeaser.vue'),
    },
    data() {
        return {
            waypoints: [{},{}],
            scrollbarScrolled: false,
            headerSwiped: false,
            waypointSortIndex: 0,
            isWaypointsDragged: false,
            searchCollapseEnabled: false,
            routeSessionInProgress: false,
            searchButtonLabel: 'Route berechnen',
            scrollbarStatus: true,
            interimMessageTabs: ['PUBLIC', 'BIKE_AND_RIDE', 'PARK_AND_RIDE', 'SHARING'],
        };
    },
    computed: {
        ...mapGetters(['user', 'mobile', 'routeSettingsChanges','nativeScrolling', 'routesLoading', 'someTransportModesLoaded', 'routeTrafficTime', 'savedRouteTime', 'selectedPOIs', 'pinDrop', 'activeRouteId', 'activeRouteMode', 'activeTransportModeTab', 'lastRoutes', 'savedRoutes', 'sidebarDetailOpen', 'interimStopsFromUrl', 'userLocation', 'interimStopMessageVisible', 'routeSettingsGeneralTypes', 'savedRouteSettingsChanges', 'savedRouteInfo', 'savedRouteClicked', 'activeTransportModeNoIntermediateStops', 'queryRouteSettings', 'queryRouteSettingsChanges', 'routeSettings', 'savedRouteSettings', 'savedRouteSettingsActive']),
        filledWaypoints() {
            return this.waypoints.filter(waypoint => waypoint.lat && waypoint.lon);
        },
        wrongWaypoints() {
            return this.waypoints.filter(waypoint => waypoint.wrongAddress);
        },
        waypointCount() {
            return this.waypoints.length;
        },
        checkIfWaypointLimitReached() {
            const waypointLimit = this.user ? LOGGEDIN_WAYPOINT_LIMIT : LOGGEDOUT_WAYPOINT_LIMIT
            return this.waypointCount >= waypointLimit
        },
        filledWaypointCount() {
            return this.filledWaypoints.length;
        },
        showRouteResults() {
            let showResults = this.filledWaypointCount > 1 && !this.interimStopsFromUrl
            if(!isEmpty(this.savedRouteInfo)) {
                showResults = this.filledWaypointCount > 1 && this.savedRouteInfo.interimStopsLoaded
            }
            return showResults
        },
        showNoResults() {
            // No results box shows up when: the address is wrong (GIS API provides no results)
            return this.wrongWaypoints.length > 0 && this.filledWaypointCount === 0;
        },
        showClearResults() {
            return this.filledWaypointCount > 0 || this.waypointCount > 2 || this.selectedPOIs.length > 0 || !this.routeTrafficTime.isNow;
        },
        showSearchOrderOptions() {
            return this.filledWaypointCount > 0 || this.waypointCount > 2;
        },
        POIMode() {
            if(this.filledWaypointCount >= 2) return 'route'
            return this.filledWaypointCount > 0 ? 'specific' : 'general';
        },
        shownRouteTime() {
            return this.savedRouteTime || this.routeTrafficTime
        },
        formattedRouteTime() {
            if(this.shownRouteTime.date && this.shownRouteTime.time) {
                return `<span>${formatDate(this.shownRouteTime.date)}</span><span class="dot-divider">${this.shownRouteTime.time}</span>`
            }
            return ''
        },
        showPinDrop() {
            return this.mobile && (this.pinDrop.data || this.pinDrop.loading)
        },
        poiPickerVisible() {
            return !this.activeRouteMode || POI_TRANSPORT_MODES.includes(this.activeRouteMode);
        },
        showSearchButton() {
           return  this.waypointCount >= 3 && this.interimMessageTabs.indexOf(this.activeTransportModeTab) === -1;
        },
        routeSettingsUpdated() {
            //show and hide red indicator based on active tab
            let changedTypes;
            if(this.savedRouteClicked && this.savedRouteSettingsChanges.length) {
                changedTypes = JSON.parse(JSON.stringify(this.savedRouteSettingsChanges));
            } else if (this.queryRouteSettingsChanges.length && this.queryRouteSettings) {
                changedTypes = JSON.parse(JSON.stringify(this.queryRouteSettingsChanges));
            } else if(this.routeSettingsChanges.length && !this.savedRouteClicked) {
                changedTypes = JSON.parse(JSON.stringify(this.routeSettingsChanges)); 
            }
            if(changedTypes?.includes(this.activeRouteMode)) 
                    return true;
            return false;       
        },
        isEmptyOverview() {
            return this.filledWaypointCount === 0 && !this.showPinDrop && this.selectedPOIs.length <= 0
        }
    },
    watch: {
        routeTrafficTime: {
            handler: function() {
                if(!this.savedRouteTime) {
                    this.autoSubmitQuery(true); //auto submit when time is updated even for interim stops
                    updateRouteUrl(this.$route, { routeTrafficTime: this.routeTrafficTime, waypoints: this.waypoints })
                }
            },
            deep: true
        },
        savedRouteInfo() {
            if (!isEmpty(this.savedRouteInfo)) this.setSavedWaypointsHandler()
        },
        filledWaypointCount(val) {
            if(!val) {
                this.resetSession();
            }
        },
        activeTransportModeTab(val) {
            if(this.activeTransportModeNoIntermediateStops && this.waypoints.length > 2) {
                const cookieName = `smr-interim-stop-message-${val.replaceAll('_', '-').toLowerCase()}`
                if(VueCookies.get(cookieName)) {
                    this.$store.commit('setInterimStopMessageVisible', false); 
                } else {
                    this.$store.commit('setInterimStopMessageVisible', true);
                    VueCookies.set(cookieName, 'true', '30d');
                }
            }   
            return val;
        },
        checkIfWaypointLimitReached() {
            if (this.checkIfWaypointLimitReached) {
                this.$store.commit('setIsWaypointLimitReached', true);
            } else {
                this.$store.commit('setIsWaypointLimitReached', false);
            }
        },
    },
    mounted() {
        this.addListeners();
        this.$store.dispatch('loadLastSearches');
        this.$store.dispatch('loadLastRoutes');
    },
    beforeDestroy() {
        this.removeListeners()
    },
    methods: {
        hideScrollbarPOIDetail() {
            this.scrollbarStatus = false;
        },
        showScrollbarPOIDetail() {
            this.scrollbarStatus = true;
        },
        searchChangeHandler({waypoint, waypointIndex}){
            this.setWaypoint(waypoint, waypointIndex)
            // this.maybeFocusOnNextSearchComponent(waypointIndex);
            // remove pin if no waypoint is filled
            if(this.filledWaypointCount === 0 && !waypoint.lat) this.removeQueryResults()
        },
        resetClickHandler() {
            // reset waypoints
            this.waypoints = [{},{}];
            this.submitQuery();
            // reset route time
            this.$store.commit('setRouteTrafficTime', { isNow: true })
            // deselect all pois
            this.$store.dispatch('deselectAllPOIs')
            this.$store.dispatch('resetQueryPoiSettings', false)
            // reset opening POI after login
            this.$store.commit('setOpenPOIAfterLogin', false)
            // remove all pindrops
            eventBus.$emit('removePinDrop', { type: 'all' })
            // reset route waypoints
            this.$store.commit('setRouteWaypoints', [])
            //reset input fields
            eventBus.$emit('clearSearchFields')
        },
        reverseClickHandler() {
            this.reverseWaypoints();
        },
        submitClickHandler() {
            // scroll to top after clicking to submit button
            const scrollbarEl = this.nativeScrolling ? this.$refs.sidebarScrollbar : this.$refs.sidebarScrollbar.$el;
            if(scrollbarEl) {
                this.$nextTick(() => {
                    scrollbarEl.scrollTop = 0;
                });
            }
            
            if(this.searchButtonLabel == 'Route neu berechnen') {
                trackEvent({ category: 'Route Planner', action: 'Destination', label: 'Update Route' }) 
                trackGa4Event({ga4Parameter1Name: 'destination', ga4Parameter1Value: 'update_route', ga4Event: 'route_planner'})
            }    
            this.$store.commit('setInterimStopsFromUrl', false)
            if(!isEmpty(this.savedRouteInfo) && !this.savedRouteInfo.interimStopsLoaded) {
                const updatedRouteInfo = {...this.savedRouteInfo, interimStopsLoaded: true}
                this.$store.commit('setSavedRouteInfo', updatedRouteInfo);
            }
            this.submitQuery();
        },
        searchFieldClickHandler(e, waypointIndex) {
            if(!this.mobile) return false;
            // If using mobile view - show a search overlay instead of using this search field
            e.preventDefault();
            this.showMobileSearchOverlay(waypointIndex);
        },
        setWaypointHandler({waypoint, waypointIndex}) {
            this.setWaypoint(waypoint, waypointIndex);
        },
        setSavedWaypointsHandler() {
            this.waypoints = JSON.parse(JSON.stringify(this.savedRouteInfo.waypoints));
            this.$store.commit('setInterimStopsFromUrl', false)
            if(this.savedRouteInfo.waypoints.length <= 2) {
                this.loadRouteForMode(this.savedRouteInfo.mode)
            } else {
                this.$store.commit('setActiveTransportModeTab', this.savedRouteInfo.mode)
                this.$store.commit('setSelectedTransportModes', [this.savedRouteInfo.mode])
            }
        },
        sidebarHeaderSwipeUpHandler() {
            eventBus.$emit('sidebarHeaderSwipeUp');
        },
        sidebarHeaderSwipeDownHandler() {
            eventBus.$emit('sidebarHeaderSwipeDown');
        },
        sidebarHeaderTouchStartHandler() {
            this.headerSwiped = true;
        },
        sidebarHeaderTouchEndHandler() {
            this.headerSwiped = false;
        },
        sidebarHeaderMovingHandler(e) {
            if(!e.changedTouches.length) return false;
            eventBus.$emit('sidebarHeaderMoving', e.changedTouches[0].screenY);
        },
        scrollToHandler(config) {
            if(config === undefined) return false
            const topOffset = config.target.offsetTop;
            const scrollbarEl = this.nativeScrolling ? this.$refs.sidebarScrollbar : this.$refs.sidebarScrollbar.$el;
            if(config.onlyScrollDown && topOffset - scrollbarEl.scrollTop < 0) return false;
            scrollbarEl.scrollTo({top: topOffset, behavior: this.nativeScrolling ? 'smooth' : 'auto'})
        },
        scrollToTopHandler() {
            const topOffset = 0;
            const scrollbarEl = this.nativeScrolling ? this.$refs.sidebarScrollbar : this.$refs.sidebarScrollbar.$el;
            scrollbarEl.scroll({top: topOffset})
        },
        addInterimClickHandler(waypointIndex) {
            this.$store.commit('setInterimStopMessageVisible', true);
            this.addInterimWaypoint(waypointIndex);
        },
        removeInterimClickHandler(waypointIndex) {
            this.$store.commit('setInterimStopMessageVisible', false);
            this.removeInterimWaypoint(waypointIndex);
        },
        routeOptionsSettingsClickHandler() {
            this.showRouteSettings();
        },
        routeOptionsTimeClickHandler() {
            this.showTimeOptions();
        },
        reloadRouteResultsHandler() {
            this.loadRouteIfWaypointsPresent();
        },
        addLocationToWaypointsHandler({waypoint, position, positionIndex, insertAfterIndex, disableLocationJump}) {
            const jumpToPoint = disableLocationJump ? false : true
            let index = 0
            if(position === 'end') index = this.waypoints.length - 1
            if(position === 'between') {
                if(positionIndex && this.waypoints[positionIndex]) {
                    index = positionIndex
                } else if(!this.user && this.checkIfWaypointLimitReached) {
                    eventBus.$emit('showLoginMenu');
                    return
                } else if(insertAfterIndex !== false) {
                    this.addInterimWaypoint(insertAfterIndex + 1)
                    index = insertAfterIndex + 1
                } else {
                    this.addInterimWaypoint(this.waypoints.length - 1)
                    index = this.waypoints.length - 2
                }
            }
            this.setWaypoint(waypoint, index, true, jumpToPoint)
        },
        loadLastSearchedRouteHandler(index) {
            let waypoints = this.lastRoutes[index];
            this.waypoints = waypoints;
            this.submitQuery();
        },
        waypointDraggedChangeHandler(dragged) {
            this.isWaypointsDragged = dragged;
        },
        updateScrollPositionHandler() {
            if(this.nativeScrolling) return false;
        },
        optionMovedHandler({oldIndex, newIndex}) {
            this.moveOption(oldIndex, newIndex);
        },
        editWaypointsWithActionsHandler(data) {
            const action = data.action
            switch(action) {
                case 'edit':
                    eventBus.$emit('focusOnSearchField', data.waypointIndex);
                    break;
                case 'remove':
                    if(this.waypoints.length > 2) {
                        this.removeInterimWaypoint(data.waypointIndex)
                        this.submitQuery()
                    }
                    break;
            }
        },

        addListeners() {
            eventBus.$on('setWaypoint', this.setWaypointHandler);
            eventBus.$on('reloadRouteResults', this.reloadRouteResultsHandler);
            eventBus.$on('scrollToSidebar', this.scrollToHandler);
            eventBus.$on('addLocationToWaypoints', this.addLocationToWaypointsHandler)
            eventBus.$on('loadLastSearchedRoute', this.loadLastSearchedRouteHandler)
            eventBus.$on('hideScrollbar', this.hideScrollbarPOIDetail);
            eventBus.$on('showScrollbar', this.showScrollbarPOIDetail);
            eventBus.$on('scrollSidebarToTop', this.scrollToTopHandler);
            eventBus.$on('reloadRouteResults', this.updateCollapseEnabled);
            eventBus.$on('editWaypointsWithAction', this.editWaypointsWithActionsHandler)
            eventBus.$on('addIndexWaypoints', this.addIndicesToWaypoints)
        },
        removeListeners() {
            eventBus.$off('setWaypoint', this.setWaypointHandler);
            eventBus.$off('reloadRouteResults', this.reloadRouteResultsHandler);
            eventBus.$off('scrollToSidebar', this.scrollToHandler);
            eventBus.$off('addLocationToWaypoints', this.addLocationToWaypointsHandler)
            eventBus.$off('loadLastSearchedRoute', this.loadLastSearchedRouteHandler)
            eventBus.$off('hideScrollbar', this.hideScrollbarPOIDetail);
            eventBus.$off('showScrollbar', this.showScrollbarPOIDetail);
            eventBus.$off('scrollSidebarToTop', this.scrollToTopHandler);
            eventBus.$off('reloadRouteResults', this.updateCollapseEnabled);
            eventBus.$off('editWaypointsWithAction', this.editWaypointsWithActionsHandler)
            eventBus.$off('addIndexWaypoints', this.addIndicesToWaypoints)
        },
        reverseWaypoints() {
            this.waypoints = this.waypoints.reverse();
            this.autoSubmitQuery();
        },
        autoSubmitQuery(forceAutoSubmit = false, jumpToPoint = true) {
            if(forceAutoSubmit || (this.waypointCount > 0 && this.waypointCount <= 2)) { // only if there are no middle points selected
                this.submitQuery(jumpToPoint);
            }
        },
        submitQuery(jumpToPoint = true) {
            if(this.waypointCount > 2 && !this.validateWaypoints()) return false;
            if(this.filledWaypointCount === 0) {
                this.removeQueryResults();
            } else if (this.filledWaypointCount === 1) {
                const position = this.waypoints[0].lat ? 'start' : 'end'
                this.showPoint({...this.filledWaypoints[0], ...{position: position}}, jumpToPoint);
            } else {
                // always use the first waypoint as user coordinates
                const firstWaypoint = this.waypoints[0]
                this.$store.commit('setUserCoordinates', {lat: firstWaypoint.lat, lon: firstWaypoint.lon})
                this.loadRoute(jumpToPoint);
            }
            this.updateCollapseEnabled();
            this.closeCollapse();
        },
        async loadRoute(fitBounds = true) {
            if(this.routesLoading) {
                return
            }
            let waypointsFormatted = this.formatWaypoints()
            this.$store.commit('setRouteWaypoints', waypointsFormatted)
            this.$store.dispatch('storeRouteLocally', [...this.waypoints]);
            await this.$store.dispatch('loadRouteResults', fitBounds);
            updateQueryParams(this.$route, { transportMode: this.activeTransportModeTab, routeOptions: this.savedRouteSettingsActive ? this.savedRouteSettings : this.routeSettings })
            this.routeSessionInProgress = true;
            this.searchButtonLabel = 'Route neu berechnen';
        },
        async loadRouteForMode(mode) {
            if(this.routesLoading) {
                return
            }
            let waypointsFormatted = this.formatWaypoints()
            this.$store.commit('setRouteWaypoints', waypointsFormatted)
            this.$store.commit('setActiveTransportModeTab', mode)
            // manually update the url because it's not done inside routeresults.vue
            updateQueryParams(this.$route, { transportMode: mode, routeOptions: this.savedRouteSettingsActive ? this.savedRouteSettings : this.routeSettings })
            // reset the selected transport modes to make all available for loading
            this.$store.commit('setSelectedTransportModes', [mode])
            await this.$store.dispatch('loadRouteResultsByType', { type: mode });
        },
        addIndicesToWaypoints() {
            // to correctly show the index for the loaded waypoints in the sidebar
            for(let index = 0; index < this.waypoints.length; index++) {
                this.$set(this.waypoints[index], 'loadedIndex', index)
            }
        },
        formatWaypoints() {
            return this.waypoints.map((waypoint, index) => {
                return {
                    id: waypoint.id ?? waypoint.name + waypoint.comment,
                    coordinate: {
                        longitude: waypoint.lon,
                        latitude: waypoint.lat,
                    },
                    label: waypoint.label,
                    name: waypoint.name, // needed for route marker popup
                    comment: waypoint.comment, // needed for route marker popup
                    realLocation: waypoint.id || waypoint.comment ? true : false,  // needed for route marker popup
                    loadedIndex: index,
                    type: waypoint.type && !CUSTOM_LOCATION_TYPES.includes(waypoint.type) ? waypoint.type : 'GEO_ADDRESS',
                }
            })
        },
        showPoint(waypoint, jumpToPoint = true) {
            eventBus.$emit('showSinglePoint', { waypoint, jumpToPoint })
            this.$store.commit('setUserCoordinates', {lat: waypoint.lat, lon: waypoint.lon})
            // reload pois on single location
            eventBus.$emit('scrollToPOIPicker');
            this.$store.dispatch('loadPOIResults');
        },
        removeQueryResults() {
            eventBus.$emit('removeSinglePoint')
            this.$store.dispatch('clearRouteResults');
            // if there is a user location, reset it to that
            if(this.userLocation) {
                this.$store.commit('setUserCoordinates', {
                    lon: this.userLocation[0],
                    lat: this.userLocation[1]
                })
            } else {
                this.$store.commit('setUserCoordinates', false)
            }
            this.$store.commit('resetWaypointPosition')
            this.$store.dispatch('resetSavedRouteSettings', false)
            this.$store.dispatch('resetQueryRouteSettings', false)
            this.searchButtonLabel = 'Route berechnen';
        },
        setWaypoint(waypoint, waypointIndex, forceAutoSubmit = false, jumpToPoint = true) {
            this.$set(this.waypoints, waypointIndex, waypoint);
            this.autoSubmitQuery(forceAutoSubmit, jumpToPoint);
            this.updateWaypointPosition(waypointIndex)
        },
        updateWaypointPosition(waypointIndex) {
            let position = 'interim'
            const value = this.waypoints[waypointIndex].lat !== undefined
            if(waypointIndex === this.waypoints.length - 1) {
                position = 'end'
            } else if(waypointIndex === 0) {
                position = 'start'
            }
            this.$store.commit('setWaypointPosition', {position, value})
        },
        addInterimWaypoint(waypointIndex) {
            let waypointsBefore = this.waypoints.slice(0, waypointIndex);
            let waypointsAfter = this.waypoints.slice(waypointIndex);
            this.waypoints = [...waypointsBefore, {}, ...waypointsAfter];
        },
        removeInterimWaypoint(waypointIndex) {
            this.waypoints.splice(waypointIndex, 1);
            this.autoSubmitQuery();
            if(this.searchCollapseEnabled) {
                this.updateCollapseEnabled();
            }
        },

        showRouteSettings() {
            trackEvent({ category: 'Route Planner', action: 'Options', label: 'Open'});
            trackGa4Event({ga4Parameter1Name: 'options', ga4Parameter1Value: 'open', ga4Parameter2Name: 'tab', ga4Parameter2Value: 'general', ga4Event: 'route_planner_settings'})
            eventBus.$emit('showGeneralSettings', { switch: 'routes' });
        },
        loadRouteIfWaypointsPresent() {
            if(this.filledWaypointCount > 1) {
                this.loadRoute();
            }
        },
        showTimeOptions() {
            trackEvent({ category: 'Route Planner', action: 'Time', label: 'Now'});
            this.$refs['time-options'].showDialog();
        },
       
        moveOption(oldIndex, newIndex) {
            this.waypoints.splice(newIndex, 0, this.waypoints.splice(oldIndex, 1)[0]);
            this.waypointSortIndex++;
            this.autoSubmitQuery();
        },
        validateWaypoints() {
            let valid = true;
            this.waypoints.forEach((waypoint, index) => {
                if(!waypoint.lat || !waypoint.lon) {
                    valid = false;
                    this.$set(this.waypoints[index], 'wrongAddress', true);
                }
            })
            return valid;
        },
        updateCollapseEnabled() {
            this.searchCollapseEnabled = this.waypointCount > TOGGLE_INTERIM_COLLAPSE_AMOUNT;
        },
        closeCollapse() {
            if(this.waypointCount > 6)
                eventBus.$emit('closeCollapse');
        },
        resetSession() {
            this.$store.commit('clearManualTransportModes');
            this.$store.dispatch('setSelectedTransportModesFromSettings');
            this.$store.commit('setActiveTransportModeTab', this.routeSettingsGeneralTypes.length ? this.routeSettingsGeneralTypes[0] : 'LIST');
            this.$store.commit('setRouteCorridor', false);

            this.routeSessionInProgress = false;
        },
        interimStopNoteCloseHandler() {
            if(this.activeTransportModeNoIntermediateStops) {
                this.$store.commit('setInterimStopMessageVisible', false);
            }
        }
    }
}
</script>

<style lang="scss">
.sidebar {
    $block: &;
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;

    .ps__rail-y {
        z-index: 100;
    }

    &__section {
        padding-left: $sidebar-horizontal-padding;
        padding-right: $sidebar-horizontal-padding;
        &--border-top {
            border-top: 1px solid $color-black-005;
        }
        &--border-bottom {
            border-bottom: 1px solid $color-black-005;
        }
        &--border-top-huge {
            border-top: 12px solid $color-black-003;
        }
        &--ordered {
            display: flex;
            flex-direction: column;

            @for $i from 0 through 4 {
                .order-#{$i} {
                    order: #{$i};
                }
            }
        }
        &--vertical-padding {
            padding-top: 23px;
            padding-bottom: 23px;
        }
        &--small-vertical-padding {
            padding-top: 12px;
            padding-bottom: 12px;
        }
        &--medium-vertical-padding {
            padding-top: 16px;
            padding-bottom: 16px;
        }
        &--larger-top-vertical-padding {
            padding-top: 24px;
            padding-bottom: 8px;

            &--top-smaller {
                padding-top: 16px;
            }
        }
    }
    &__drawer {
        height: 30px;
        position: absolute;
        top: 0;
        left: 50px;
        right: 50px;
        z-index: 10;
        @include mq(tablet) {
            display: none;
        }
        &.is-header-swiped {
            &::after {
                background-color: $color-grey;
            }
        }
        &::after {
            content: '';
            transition: background-color 0.15s;
            position: absolute;
            left: 50%;
            top: 5px;
            height: 5px;
            width: 35px;
            border-radius: 2.5px;
            background-color: $color-black-015;
            transform: translateX(-50%);
            z-index: 5;
        }
    }
    &__header {
        padding-top: 18px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-bottom: 6px;
        z-index: 3;
        flex-shrink: 0;
        user-select: none;
        position: relative;

        @include mq(tablet) {
            padding-top: 20px;
        }
    }

    &__scrollbar {
        min-height: 100%;
        padding-bottom: 16px;
        @include mq($until: tablet) {
            height: 100%;
        }
        .ps__rail-y {
            // z-index: 10;
            z-index: 100;
        }

        &--native {
            overflow-y: scroll;
            overflow-x: hidden;
            .is-mobile-sidebar-size--minimal &{
                overflow: hidden;
            }
        }

    }

    &__route-options {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 14px;
        &__time {
            display: flex;
            align-items: center;
            padding-left: 12px;
            padding-right: 10px;
            cursor: pointer;
            .no-touch &:hover {
                #{$block}__route-options__time__icon {
                    color: $color-black;
                }
            }
            &__icon {
                transition: color 0.15s;
                margin-right: 12px;
                color: $color-grey-dark;
            }
        }
        &__settings {
            display: flex;
            align-items: center;
            cursor: pointer;
            .no-touch &:hover {
                #{$block}__route-options__settings__icon {
                    color: $color-black;
                }
            }
            &__text {
                margin-right: 2px;
                font-weight: $font-weight-bold;
            }
            &__icon {
                position: relative;
                transition: color 0.15s;
                color: $color-grey-dark;
                &.options-changed {
                    @include red-dot-change-indicator;
                }
                svg {
                    display: block;
                }
            }
        }
    }
    &__submit {
        margin-bottom: 24px;
    }
    &__action-icon {
        cursor: pointer;
        color: $color-grey-dark;
        .no-touch &:hover {
            color: $color-black;
        }
        &.disabled {
            pointer-events: none;
            color: $color-grey-silver;
        }
        svg {
            display: block;
        }
    }

    &__routes {
        position: relative;
        z-index: 1;
    }
}
</style>